// frontend/src/pages/Checkout.js
import React, { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import axios from 'axios';

const Checkout = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [processing, setProcessing] = useState(false);
  const [paymentStatus, setPaymentStatus] = useState(null);

  const { transactionId, plan, precio, email } = location.state || {};

  // Solo el storeId es público — el token lo maneja el backend
  const PAYPHONE_STORE_ID = process.env.REACT_APP_PAYPHONE_STORE_ID;
  const BASE_URL = process.env.REACT_APP_API_URL || '/api';

  useEffect(() => {

    if (!transactionId || !plan) {
      toast.error('Datos de pago inválidos');
      navigate('/register');
      return;
    }

    const loadPayphoneResources = () => {
      if (!document.querySelector('#payphone-css')) {
        const link = document.createElement('link');
        link.id = 'payphone-css';
        link.rel = 'stylesheet';
        link.href = 'https://cdn.payphonetodoesposible.com/box/v1.1/payphone-payment-box.css';
        document.head.appendChild(link);
      }

      if (!document.querySelector('#payphone-script')) {
        const script = document.createElement('script');
        script.id = 'payphone-script';
        script.src = 'https://cdn.payphonetodoesposible.com/box/v1.1/payphone-payment-box.js';
        script.type = 'module';
        script.async = true;
        script.onload = () => {
          console.log('✅ Script de Payphone cargado');
          initializePayphoneButton();
        };
        script.onerror = () => {
          toast.error('Error al cargar el módulo de pagos. Recarga la página.');
        };
        document.body.appendChild(script);
      } else {
        initializePayphoneButton();
      }
    };

    loadPayphoneResources();
  }, [transactionId, plan, precio, navigate]);

  const initializePayphoneButton = () => {
    if (document.readyState === 'loading') {
      window.addEventListener('DOMContentLoaded', renderPayphoneButton);
    } else {
      renderPayphoneButton();
    }
  };

  const renderPayphoneButton = async () => {
    if (!window.PPaymentButtonBox) {
      console.error('❌ PPaymentButtonBox no disponible');
      toast.error('Error al cargar el botón de pagos');
      return;
    }

    try {
      const subtotal = Math.round(precio * 100);

      // Obtener config del backend — el token NUNCA sale al frontend
      const { data: config } = await axios.get(`${BASE_URL}/payments/payphone-config`, {
        params: {
          transactionId,
          amount: subtotal,
          plan,
          email,
        }
      });

      // responseUrl debe incluir el basename /agenda para que React Router lo reconozca
      config.responseUrl = `${window.location.origin}/agenda/checkoutconfirm`;

      const payphone = new window.PPaymentButtonBox(config);
      payphone.render('pp-button');

      console.log('✅ Botón de Payphone renderizado');
    } catch (error) {
      console.error('❌ Error:', error);
      toast.error('Error al inicializar el módulo de pagos');
    }
  };

  // Escuchar eventos de pago (la documentación indica que se usa responseUrl)
  useEffect(() => {
    // Verificar si venimos de un pago (parámetros en URL)
    const urlParams = new URLSearchParams(window.location.search);
    const paymentId = urlParams.get('id');
    const clientTxId = urlParams.get('clientTransactionId');
    
    if (paymentId && clientTxId) {
      // Hay un pago pendiente de confirmar
      confirmPaymentWithBackend(paymentId, clientTxId);
    }
  }, []);

  const confirmPaymentWithBackend = async (paymentId, clientTxId) => {
    setProcessing(true);
    try {
      const response = await axios.post(`${BASE_URL}/payments/confirm-payment`, {
        paymentId,
        transactionId: clientTxId
      });

      if (response.data.success) {
        setPaymentStatus('success');
        toast.success('¡Pago exitoso! Tu cuenta ha sido activada.');
        setTimeout(() => navigate('/login', {
          state: { message: '¡Cuenta activada exitosamente!' }
        }), 3000);
      } else {
        setPaymentStatus('failed');
        toast.error(response.data.error || 'Error al activar la cuenta');
      }
    } catch (error) {
      console.error('Error confirmando pago:', error);
      setPaymentStatus('failed');
      toast.error('Error al procesar el pago');
    } finally {
      setProcessing(false);
    }
  };

  // Estilos (igual que antes)
  const styles = {
    container: {
      minHeight: '100vh',
      background: 'linear-gradient(135deg, #f0f9ff 0%, #ffffff 50%, #eff6ff 100%)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      padding: '2rem',
      position: 'relative'
    },
    card: {
      backgroundColor: 'rgba(255, 255, 255, 0.95)',
      backdropFilter: 'blur(10px)',
      borderRadius: '32px',
      boxShadow: '0 25px 50px -12px rgba(59, 130, 246, 0.5)',
      maxWidth: '500px',
      width: '100%',
      padding: '2rem',
      textAlign: 'center',
      position: 'relative',
      overflow: 'hidden'
    },
    title: {
      fontSize: '1.8rem',
      fontWeight: '800',
      color: '#111827',
      marginBottom: '1rem'
    },
    planCard: {
      background: '#f3f4f6',
      borderRadius: '16px',
      padding: '1rem',
      margin: '1.5rem 0'
    },
    price: {
      fontSize: '2rem',
      fontWeight: '800',
      color: '#3b82f6'
    },
    buttonContainer: {
      marginTop: '2rem',
      minHeight: '200px'
    },
    loading: {
      textAlign: 'center',
      padding: '2rem',
      color: '#6b7280'
    },
    success: {
      backgroundColor: '#d1fae5',
      color: '#065f46',
      padding: '1rem',
      borderRadius: '12px',
      marginTop: '1rem'
    },
    error: {
      backgroundColor: '#fee2e2',
      color: '#991b1b',
      padding: '1rem',
      borderRadius: '12px',
      marginTop: '1rem'
    }
  };

  return (
    <div style={styles.container}>
      <div style={{
        position: 'absolute',
        top: '-150px',
        left: '-100px',
        width: '400px',
        height: '400px',
        background: 'radial-gradient(circle, rgba(59, 130, 246, 0.15) 0%, transparent 70%)',
        borderRadius: '50%',
        filter: 'blur(40px)',
        pointerEvents: 'none'
      }} />
      
      <div style={{
        position: 'absolute',
        bottom: '-150px',
        right: '-100px',
        width: '500px',
        height: '500px',
        background: 'radial-gradient(circle, rgba(59, 130, 246, 0.1) 0%, transparent 70%)',
        borderRadius: '50%',
        filter: 'blur(60px)',
        pointerEvents: 'none'
      }} />

      <div style={styles.card}>
        <div style={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          height: '4px',
          background: 'linear-gradient(90deg, #3b82f6, #93c5fd, #3b82f6)',
          borderTopLeftRadius: '32px',
          borderTopRightRadius: '32px'
        }} />

        <h2 style={styles.title}>Finalizar Suscripción</h2>
        
        <div style={styles.planCard}>
          <p style={{ margin: '0 0 0.5rem 0', color: '#6b7280' }}>Plan seleccionado</p>
          <h3 style={{ margin: 0, fontSize: '1.5rem' }}>{plan}</h3>
          <div style={{ marginTop: '0.5rem' }}>
            <span style={styles.price}>${precio}</span>
            <span style={{ color: '#6b7280' }}>/mes</span>
          </div>
          <p style={{ margin: '0.5rem 0 0 0', fontSize: '0.875rem', color: '#6b7280' }}>
            {email}
          </p>
        </div>

        {paymentStatus === 'success' && (
          <div style={styles.success}>
            ✅ ¡Pago exitoso! Redirigiendo al login...
          </div>
        )}

        {paymentStatus === 'failed' && (
          <div style={styles.error}>
            ❌ Error al procesar el pago. Por favor, intenta nuevamente.
          </div>
        )}

        {processing && (
          <div style={styles.loading}>
            <div>⏳ Procesando tu pago...</div>
            <div style={{ fontSize: '0.875rem', marginTop: '0.5rem' }}>
              No cierres esta ventana
            </div>
          </div>
        )}

        <div id="pp-button" style={styles.buttonContainer}>
          <div style={styles.loading}>Cargando módulo de pagos...</div>
        </div>

        <button
          onClick={() => navigate('/register')}
          style={{
            marginTop: '1rem',
            padding: '0.75rem',
            backgroundColor: 'transparent',
            color: '#6b7280',
            border: '1px solid #e5e7eb',
            borderRadius: '12px',
            cursor: 'pointer',
            width: '100%',
            transition: 'all 0.2s'
          }}
        >
          ← Volver al registro
        </button>
      </div>
    </div>
  );
};

export default Checkout;